import clsx from "clsx";
import { ReactNode, useState } from "react";

import { useSearchWithDefault } from "@smart/itops-hooks-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { inIframe } from "@smart/itops-utils-basic";

import { BrandingWebView } from "./branding/branding-web-view";
import { ClientMessages } from "./client-message";
import { GeneralSettings } from "./general";
import { GenerateHTML } from "./generate-html";
import { SettingsLauncher } from "./launcher";
import { SettingsTabKey, useSettingsTabs } from "./tabs";
import { VoiceGuideSettings } from "./voice-guide";

const TabButton = ({
  name,
  label,
  activeTab,
  setActiveTab,
}: {
  name: string;
  label: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) => (
  <button
    type="button"
    className={`px-[1.2rem] py-[1rem] font-semibold rounded ${
      activeTab === name
        ? "bg-cyan-140"
        : "text-neutral-600 hover:bg-neutral-50"
    }`}
    onClick={() => setActiveTab(name)}
  >
    {label}
  </button>
);

type SettingsViewProps = {
  expanded?: boolean;
};

export const SettingsView = ({ expanded }: SettingsViewProps) => {
  const { tab } = useSearchWithDefault((p) => ({ tab: p.get("tab") }));
  const [activeTab, setActiveTab] = useState(tab || "web-enquiries");
  const { hostType } = useSmokeballApp();

  const isLauncher = hostType === "windows" && !expanded && !inIframe();

  const tabComponents: Record<SettingsTabKey, ReactNode> = {
    "web-enquiries": <GenerateHTML />,
    "client-messages": <ClientMessages />,
    general: <GeneralSettings />,
    branding: <BrandingWebView />,
    "voice-guide": <VoiceGuideSettings />,
  };
  const tabs = useSettingsTabs().map((t) => ({
    ...t,
    component: tabComponents[t.key],
  }));

  const currentTab = tabs.find((t) => t.key === activeTab) || tabs[0];

  return (
    <div
      className={clsx(
        "flex justify-center h-screen text-ui",
        !isLauncher && "bg-gray-100",
      )}
    >
      <div
        className={clsx(
          "flex flex-col flex-1 p-[2rem] pb-[4rem]",
          isLauncher && "max-w-[88rem]",
        )}
      >
        {!isLauncher && (
          <div className="flex mb-6 gap-small">
            {tabs.map((t) => (
              <TabButton
                key={t.key}
                name={t.key}
                label={t.label}
                activeTab={currentTab.key}
                setActiveTab={setActiveTab}
              />
            ))}
          </div>
        )}
        <div className="bg-white rounded flex-1 min-h-0">
          {isLauncher ? <SettingsLauncher /> : currentTab.component}
        </div>
      </div>
    </div>
  );
};
